// @ts-nocheck
import React from 'react'
import * as R from 'ramda'

// +vailable space around DOMRect box in viewPort
// (DOMRect, viewPort) -> { direction: pixels }
const spaceAroundElement = (
  { left, top, right, bottom },
  { width, height },
) => ({
  left,
  top,
  right: width - right,
  bottom: height - bottom,
})

// Which cardinal direction from anchor node to place tethered child node
const tetherDirection = (
  { top, left, right, bottom }, // anchor position
  { height, width }, // child size
  order, // preferred position
) => {
  // is there enough room to place child
  const lookup = R.flip(R.prop)
  const roomEnough = R.find(
    lookup({
      t: top > height,
      l: left > width,
      b: bottom > height,
      r: right > width,
      c: true,
    }),
  )
  // first direction with enough free space
  return R.pipe(
    R.either(R.trim, R.always('b t r l c')),
    R.toLower,
    R.split(/[-,\s]+/g),
    R.map(R.head),
    R.converge(R.defaultTo, [R.head, roomEnough]),
    lookup({
      t: 'top',
      l: 'left',
      b: 'bottom',
      r: 'right',
      c: 'center',
    }),
  )(order)
}

const windowSize = () => ({
  width: document.documentElement.clientWidth,
  height: document.documentElement.clientHeight,
})

const getDimensions = el => {
  const { top, left, bottom, right, width, height } = el.getBoundingClientRect()
  const [cx, cy] = [left + width / 2, top + height / 2]
  return { top, left, bottom, right, width, height, cx, cy }
}

// place tethered child using css
// (Element, Element, [String]) -> String
export const placeTetheredChild = (
  anchorNode,
  childNode,
  order = [],
  shift = 0,
) => {
  if (!(anchorNode instanceof Element))
    throw new Error('anchor node must be a DOM Element')
  if (!(childNode instanceof Element)) {
    throw new Error('child node must be a DOM Element')
  }
  const viewPort = windowSize()
  const anchor = getDimensions(anchorNode)
  const child = getDimensions(childNode)
  child.padding = getPixelSize('padding-top')(childNode) + 1

  // get cardinal direction to shift tethered child element relative to anchor
  const space = spaceAroundElement(anchor, viewPort)
  const direction = tetherDirection(space, child, order)

  // calculate x and y offset in pixels
  let x = R.pipe(
    R.cond([
      [R.equals('left'), () => anchor.left - child.width],
      [R.equals('right'), () => anchor.right],
      [R.T, R.always(anchor.cx - child.width / 2)],
    ]),
    R.min(viewPort.width - child.width),
    R.max(0),
    Math.round,
  )(direction)

  let y = R.pipe(
    R.cond([
      [R.equals('top'), R.always(anchor.top - child.height)],
      [R.equals('bottom'), R.always(anchor.bottom)],
      [R.T, R.always(anchor.cy - child.height / 2)],
    ]),
    R.min(viewPort.height - child.height),
    R.max(0),
    Math.round,
  )(direction)

  if (direction === 'right') x += shift * child.width
  else if (direction === 'left') x -= shift * child.width
  else if (direction === 'top') y -= shift * child.height
  else if (direction === 'bottom') y += shift * child.height

  const [tx, ty] = [x + window.scrollX, y + window.scrollY].map(Math.round)

  return {
    arrowTransform:
      getArrowTransform({ x, y, direction, child, anchor }) +
      (shift ? ' translateY(100%)' : ''),
    childTransform: `translate(${tx}px, ${ty}px)`,
  }
}

// get pixel size of css property
// String -> Element -> Number
const getPixelSize = property =>
  R.pipe(
    R.tryCatch(
      element => window.getComputedStyle(element).getPropertyValue(property),
      R.always('0px'),
    ),
    parseInt,
  )

const getArrowTransform = ({
  x,
  y,
  direction,
  anchor,
  child,
  arrowSize = 14,
}) => {
  return R.pipe(
    key =>
      ({
        top: { translateY: R.max(0, child.height - arrowSize), rotate: 2 },
        left: { translateX: R.max(0, child.width - arrowSize), rotate: 1 },
        right: { translateX: 0, rotate: 3 },
        bottom: { translateY: 0, rotate: 0 },
      }[key]),
    R.defaultTo({
      translateX: (child.width - arrowSize) / 2,
      translateY: (child.height - arrowSize) / 2,
      scale: 0.1,
    }),
    R.mergeRight({
      translateX: R.pipe(
        R.add(arrowSize * -0.5),
        R.max(child.padding),
        R.min(child.width - child.padding - arrowSize),
      )(anchor.cx - x),
      translateY: R.pipe(
        R.add(arrowSize * -0.5),
        R.max(child.padding),
        R.min(child.height - child.padding - arrowSize),
      )(anchor.cy - y),
    }),
    ({ translateX = 0, translateY = 0, scale = 1, rotate = 0 }) => [
      `translateX(${Math.round(translateX)}px)`,
      `translateY(${Math.round(translateY)}px)`,
      `rotate(${rotate / 4}turn)`,
      `scale(${scale})`,
    ],
    R.join(' '),
  )(direction)
}

// get or create the parent dom node for tether portals
const getTetherRoot = (id = 'tether-root') => {
  let node = document.getElementById(id)
  if (!node) {
    node = document.createElement('div')
    node.setAttribute('id', id)
    document.body.appendChild(node)
  }
  return node
}

// DOM Element visible in viewport
// Element -> Boolean
export const isElementVisible = element => {
  if (!element) return false
  const { width, height } = windowSize()
  const { top, bottom, left, right } = element.getBoundingClientRect()
  return bottom > 0 && right > 0 && left < width && top < height
}

// () -> node
export const usePortalContainer = () => {
  const portalRef = React.useRef(null)
  if (!portalRef.current) {
    const el = document.createElement('div')
    el.className = 'portalContainer'
    portalRef.current = el
  }
  React.useEffect(() => {
    // default parent is "body"
    const parent = getTetherRoot()
    parent.appendChild(portalRef.current)
    return () => portalRef.current.remove()
  }, [])
  return portalRef.current
}
